import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { HomeIcon, ArrowLeftIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import SEO from '../components/SEO';

const NotFoundPage = () => {
    return (
        <>
            <SEO 
                title="404 - Page Not Found | AircoreDynamics"
                description="The page you are looking for could not be found."
                keywords="404, page not found, error"
                image="https://aircoredynamics.com/og-image.png"
                url="/404"
                type="website"
                noindex={true}
            />
            <div className="min-h-[70vh] flex items-center justify-center px-6 mt-8">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-center max-w-2xl"
                >
                    {/* 404 Animation */}
                    <motion.div
                        initial={{ scale: 0.5, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ delay: 0.2, type: "spring" }}
                        className="mb-8"
                    >
                        <div className="relative inline-block">
                            <h1 className="text-[150px] md:text-[200px] font-bold bg-gradient-to-r from-light-primary to-blue-500 dark:from-dark-primary dark:to-cyan-400 bg-clip-text text-transparent leading-none">
                                404
                            </h1>
                            <motion.div
                                animate={{ 
                                    rotate: [0, 10, -10, 0],
                                }}
                                transition={{ 
                                    duration: 2,
                                    repeat: Infinity,
                                    ease: "easeInOut"
                                }}
                                className="absolute -top-4 -right-4 text-6xl"
                            >
                                ✈️
                            </motion.div>
                        </div>
                    </motion.div>

                    {/* Message */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.4 }}
                        className="space-y-4 mb-8"
                    >
                        <h2 className="text-3xl md:text-4xl font-bold">
                            Page Not Found
                        </h2>
                        <p className="text-lg opacity-70 max-w-md mx-auto">
                            Oops! The page you're looking for has taken off to unknown coordinates. 
                            Let's navigate you back to familiar airspace.
                        </p>
                    </motion.div>

                    {/* Action Buttons */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.6 }}
                        className="flex flex-col sm:flex-row gap-4 justify-center"
                    >
                        <Link 
                            to="/" 
                            className="btn-primary flex items-center justify-center gap-2"
                        >
                            <HomeIcon className="h-5 w-5" />
                            Back to Home
                        </Link>
                        
                        <button 
                            onClick={() => window.history.back()}
                            className="btn-secondary flex items-center justify-center gap-2"
                        >
                            <ArrowLeftIcon className="h-5 w-5" />
                            Go Back
                        </button>
                        
                        <Link 
                            to="/courses" 
                            className="btn-secondary flex items-center justify-center gap-2"
                        >
                            <MagnifyingGlassIcon className="h-5 w-5" />
                            Browse Courses
                        </Link>
                    </motion.div>

                    {/* Popular Links */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.8 }}
                        className="mt-12 pt-8 border-t border-light-border dark:border-dark-border"
                    >
                        <p className="text-sm opacity-60 mb-4">Popular pages:</p>
                        <div className="flex flex-wrap gap-3 justify-center">
                            <Link to="/courses" className="text-sm hover:text-light-primary dark:hover:text-dark-primary transition-colors">
                                Courses
                            </Link>
                            <span className="opacity-30">•</span>
                            <Link to="/articles" className="text-sm hover:text-light-primary dark:hover:text-dark-primary transition-colors">
                                Articles
                            </Link>
                            <span className="opacity-30">•</span>
                            <Link to="/news" className="text-sm hover:text-light-primary dark:hover:text-dark-primary transition-colors">
                                News
                            </Link>
                            <span className="opacity-30">•</span>
                            <Link to="/research" className="text-sm hover:text-light-primary dark:hover:text-dark-primary transition-colors">
                                Research
                            </Link>
                            <span className="opacity-30">•</span>
                            <Link to="/contact" className="text-sm hover:text-light-primary dark:hover:text-dark-primary transition-colors">
                                Contact
                            </Link>
                        </div>
                    </motion.div>
                </motion.div>
            </div>
        </>
    );
};

export default NotFoundPage;
